/**
 * Shell-level dispatcher for adapter-emitted `CartMutationEvent`s.
 *
 * Adapters call `onCartMutation(event)`; this hook returns a handler that
 * translates each event into `cartSlice` / `productConfigSlice` calls. It
 * lives in the shell (not in adapters) because it imports Zustand — which
 * is forbidden inside `adapters/**` by the `no-restricted-imports` rule.
 *
 * Idempotency:
 *   - `select-extra`: the same extra at the same count is a no-op; a NUMBER
 *     extra whose count moved is not.
 *   - `unselect-extra`: removing a non-present extra is a no-op.
 *
 * Both extra cases rebuild the line by SPREADING it. Listing the fields by
 * hand dropped every one that was not listed — `aiEditing` among them, so
 * selecting an extra silently cleared a line's sky-replacement settings.
 *
 * Error handling: invalid events (missing cart item, no cart context) are
 * silently dropped. Adapters fire optimistically; the dispatcher decides.
 */

import { useCallback } from 'react'

import type {
  CartItem,
  CartItemPriceSnapshot,
  ResolvedCatalogue,
  SalesforceId
} from 'models/purchaseFlowRevamp'

import type {
  CartMutationEvent,
  CartMutationHandler
} from '../../adapters/bridge'
import { computeDrawerPricing } from '../../components/productDrawer/useDrawerPricing'
import { usePfStore } from '../../store'

/**
 * Re-prices a line after its extras changed, using the same computation the
 * drawer priced it with. Without this the cart keeps quoting the price the
 * drawer snapshotted while the order bills the extra — the two disagree by
 * exactly the extra.
 *
 * Returns the previous snapshot untouched when the catalogue can't price the
 * line (not loaded, product gone): a stale price beats a blank one, and the
 * order's own validation is the backstop.
 */
const repricedSnapshot = (
  item: CartItem,
  extraServiceIds: SalesforceId[],
  extraQuantities: Record<SalesforceId, number> | undefined,
  catalogue: ResolvedCatalogue | undefined
): CartItemPriceSnapshot | undefined => {
  const pricing = computeDrawerPricing(
    catalogue,
    item.shellProductId,
    item.variantId,
    item.optionValues,
    extraServiceIds,
    item.quantity,
    extraQuantities ?? {}
  )
  if (!pricing.isPriced || !pricing.unitPrice || !pricing.currency) return item.priceSnapshot

  return {
    unitPrice: pricing.unitPrice.toString(),
    addonsPrice: pricing.addonsPrice?.gt(0) ? pricing.addonsPrice.toString() : null,
    // Re-priced together with the unit, so an extras edit cannot leave a stale strike.
    baseUnitPrice: pricing.baseUnitPrice?.toString() ?? null,
    currency: pricing.currency,
    resolvedAt: new Date().toISOString(),
  }
}

/**
 * Returns a stable `CartMutationHandler` that dispatches adapter cart-mutation
 * intents into the PF store.
 *
 * Takes the catalogue rather than fetching it: the shell that mounts the
 * adapters already holds it, and re-deriving country/locale here would be a
 * second source of truth for which catalogue prices a line.
 *
 * Usage:
 *   const onCartMutation = useCartMutationDispatcher(catalogue)
 *   <StagingAdapter onCartMutation={onCartMutation} ... />
 */
export function useCartMutationDispatcher(catalogue?: ResolvedCatalogue): CartMutationHandler {
  const replaceItem = usePfStore((s) => s.replaceItem)

  return useCallback<CartMutationHandler>((event: CartMutationEvent) => {
    const state = usePfStore.getState()
    const items = state.items

    switch (event.type) {

      case 'select-extra': {
        const { productId, parentCartItemId, count } = event
        // Associate the extra with the specified parent cart item.
        // Without a parent id we can't attach — drop silently (adapter bug).
        if (!parentCartItemId) return

        const item = items.find((i) => i.id === parentCartItemId)
        if (!item) return

        const alreadyOn = item.extraServiceIds.includes(productId)
        const wanted = count === undefined ? undefined : Math.max(1, Math.floor(count))
        // Idempotent in both dimensions: the same extra at the same count is
        // nothing to do, but a NUMBER extra whose count moved still is.
        if (alreadyOn && item.extraQuantities?.[productId] === wanted) return

        const { id: _id, ...rest } = item
        const nextExtraIds = alreadyOn ? item.extraServiceIds : [...item.extraServiceIds, productId]
        const nextQuantities = wanted === undefined
          ? item.extraQuantities
          : { ...item.extraQuantities, [productId]: wanted }
        replaceItem(parentCartItemId, {
          ...rest,
          extraServiceIds: nextExtraIds,
          extraQuantities: nextQuantities,
          priceSnapshot: repricedSnapshot(item, nextExtraIds, nextQuantities, catalogue),
        })
        return
      }

      case 'unselect-extra': {
        const { productId, parentCartItemId } = event
        if (!parentCartItemId) return

        const item = items.find((i) => i.id === parentCartItemId)
        if (!item) return

        // Idempotent: skip if already absent.
        if (!item.extraServiceIds.includes(productId)) return

        const { id: _id, ...rest } = item
        const { [productId]: _dropped, ...remainingCounts } = item.extraQuantities ?? {}
        const nextExtraIds = item.extraServiceIds.filter((id) => id !== productId)
        replaceItem(parentCartItemId, {
          ...rest,
          extraServiceIds: nextExtraIds,
          extraQuantities: remainingCounts,
          priceSnapshot: repricedSnapshot(item, nextExtraIds, remainingCounts, catalogue),
        })
        return
      }
    }
  }, [replaceItem, catalogue])
}
